import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { error, failedResponse, getBackendUrl, INVALID_ID, loaded, loading, LoadStateInfo, notStarted } from "../common";
import { SubjectPerformedInfo } from "./model";
import { getToken } from "../../features/user/UserAvatar";

export interface SubjectPerformedInsUpdState {
  state: LoadStateInfo;
  subjectPerformed: SubjectPerformedInfo;
}

const initialState: SubjectPerformedInsUpdState = {
  state: notStarted(),
  subjectPerformed: emptySubjectPerformed(),
};

function emptySubjectPerformed(): SubjectPerformedInfo {
  return {
    id: Number(INVALID_ID),
    schoolId: Number(INVALID_ID),
    classroomId: Number(INVALID_ID),
    subjectId: Number(INVALID_ID),
  };
}

export interface SubjectPerformedRetrieveRequest {
  subjectPerformedId: string | number;
}

export const subjectPerformedRetrieve = createAsyncThunk(
  "subjectPerformedRetrieve",
  async (param: SubjectPerformedRetrieveRequest, thunkAPI) => {
    console.debug(`retrieve subject performed ${param.subjectPerformedId}`);
    const response = await fetch(`${getBackendUrl()}/api/subject-performed/${param.subjectPerformedId}`, {
      signal: thunkAPI.signal,
      headers: { "Authorization": `Bearer ${getToken()}` }
    });
    if (response.ok) return await response.json();
    return thunkAPI.rejectWithValue(failedResponse(response));
  }
);

export interface SubjectPerformedInsertRequest {
  subjectPerformed: SubjectPerformedInfo;
}
export const subjectPerformedInsert = createAsyncThunk(
  "subjectPerformedInsert",
  async (param: SubjectPerformedInsertRequest, thunkAPI) => {
    console.debug("insert subject performed");
    const response = await fetch(`${getBackendUrl()}/api/subject-performed`, {
      method: "POST",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.subjectPerformed),
    });
    if (response.ok) return await response.json();
    return thunkAPI.rejectWithValue(failedResponse(response));
  }
);

export interface SubjectPerformedUpdateRequest {
  subjectPerformedId: number;
  subjectPerformed: SubjectPerformedInfo;
}
export const subjectPerformedUpdate = createAsyncThunk(
  "subjectPerformedUpdate",
  async (param: SubjectPerformedUpdateRequest, thunkAPI) => {
    console.debug("update subject performed", param);
    const _body = JSON.stringify(param.subjectPerformed);
    const response = await fetch(`${getBackendUrl()}/api/subject-performed/${param.subjectPerformedId}`, {
      method: "PUT",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: _body,
    });
    if (response.ok) return await response.json();
    return thunkAPI.rejectWithValue(failedResponse(response));
  }
);

export interface SubjectPerformedDeleteRequest {
  subjectPerformedId: string | number;
}
export const subjectPerformedDelete = createAsyncThunk(
  "subjectPerformedDelete",
  async (param: SubjectPerformedDeleteRequest, thunkAPI) => {
    console.debug("delete subject performed");
    const response = await fetch(`${getBackendUrl()}/api/subject-performed/${param.subjectPerformedId}`, {
      method: "DELETE",
      signal: thunkAPI.signal,
      headers: { "Authorization": `Bearer ${getToken()}` },
    });
    if (response.ok) return { subjectPerformedId: param.subjectPerformedId };
    return thunkAPI.rejectWithValue(failedResponse(response));
  }
);

export const subjectPerformedInsUpdSlice = createSlice({
  name: "subjectPerformedInsUpd",
  initialState,
  reducers: {
    set: (state, action: PayloadAction<SubjectPerformedInfo>) => {
      state.state = notStarted();
      state.subjectPerformed = action.payload;
    },
    clean: (state) => {
      state.state = notStarted();
      state.subjectPerformed = emptySubjectPerformed();
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(subjectPerformedRetrieve.pending, (state) => { state.state = loading(); })
      .addCase(subjectPerformedRetrieve.fulfilled, (state, action) => {
        state.state = loaded();
        state.subjectPerformed = action.payload;
      })
      .addCase(subjectPerformedRetrieve.rejected, (state, action) => { state.state = error(action); })

      .addCase(subjectPerformedInsert.pending, (state) => { state.state = loading(); })
      .addCase(subjectPerformedInsert.fulfilled, (state, action) => {
        state.state = loaded();
        state.subjectPerformed = action.payload;
      })
      .addCase(subjectPerformedInsert.rejected, (state, action) => { state.state = error(action); })

      .addCase(subjectPerformedUpdate.pending, (state) => { state.state = loading(); })
      .addCase(subjectPerformedUpdate.fulfilled, (state, action) => {
        state.state = loaded();
        state.subjectPerformed = action.payload;
      })
      .addCase(subjectPerformedUpdate.rejected, (state, action) => { state.state = error(action); })

      .addCase(subjectPerformedDelete.pending, (state) => { state.state = loading(); })
      .addCase(subjectPerformedDelete.fulfilled, (state) => {
        state.state = loaded();
        state.subjectPerformed = emptySubjectPerformed();
      })
      .addCase(subjectPerformedDelete.rejected, (state, action) => { state.state = error(action); });
  },
});

export const selectSubjectPerformedInsUpd = (state: RootState) => state.subjectPerformedInsUpd;

export default subjectPerformedInsUpdSlice.reducer;
